import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Component } from 'react';
import { HOST_MODES } from '@/constants';
import { useMainLayoutStore } from '@/shared/zustand/mainLayoutStore';
import useToastStore from '@/shared/zustand/toastStore';
import { AlertCircle } from 'lucide-react';
// Higher-order component to inject the store values into the class component
const withStoreValues = (WrappedComponent) => {
    return (props) => {
        const { modesHistory } = useMainLayoutStore();
        const showToast = useToastStore((state) => state.showToast);
        const lastOpenedMode = modesHistory.length > 0
            ? modesHistory[modesHistory.length - 1]?.mode
            : null;
        return (_jsx(WrappedComponent, { ...props, lastOpenedMode: lastOpenedMode, showToast: showToast }));
    };
};
class ModeSwitcherErrorBoundaryBase extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorMode: null
        };
    }
    static getDerivedStateFromError(_) {
        // Update state so the next render will show the fallback UI
        return {
            hasError: true,
            error: null,
            errorInfo: null,
            errorMode: null
        };
    }
    componentDidCatch(error, errorInfo) {
        // Capture the error details
        this.setState({
            error,
            errorInfo,
            errorMode: this.props.lastOpenedMode
        });
        // Log the error to console
        console.error('Mode Error:', {
            mode: this.props.lastOpenedMode,
            error,
            errorInfo
        });
        // Show a toast notification
        this.props.showToast('error', 'Error', `Error in : ${this.props.lastOpenedMode || 'Unknown'}`);
    }
    getModeTypeLabel(mode) {
        if (!mode)
            return 'Unknown';
        const hostModeValues = Object.values(HOST_MODES);
        return hostModeValues.includes(mode) ? 'Host Mode' : 'Remote Mode';
    }
    render() {
        if (this.state.hasError) {
            const { error, errorInfo, errorMode } = this.state;
            const modeType = this.getModeTypeLabel(errorMode);
            return (_jsx("div", { className: "w-full h-full flex flex-col items-center justify-center p-6 bg-gray-50", children: _jsxs("div", { className: "max-w-lg w-full bg-white rounded-lg shadow-lg p-8 text-center", children: [_jsx("div", { className: "flex justify-center mb-4", children: _jsx(AlertCircle, { size: 64, className: "text-red-500" }) }), _jsx("h1", { className: "text-2xl font-bold text-gray-800 mb-2 font-heading", children: "Module Error Detected" }), _jsxs("p", { className: "text-gray-600 mb-4", children: ["The ", _jsx("span", { className: 'font-semibold', children: errorMode || 'current' }), " (", modeType, ") has encountered an error and is currently unavailable."] }), _jsx("p", { className: "text-sm text-gray-500 mb-4", children: "Please check the console for more details." })] }) }));
        }
        return this.props.children;
    }
}
const ModeSwitcherErrorBoundary = withStoreValues(ModeSwitcherErrorBoundaryBase);
export default ModeSwitcherErrorBoundary;
